import { useState } from "react";
import {
  listTrails,
  createClaim,
  proposeTrail,
  ApiError,
  type TrailListItem,
  type NameConflict,
} from "../api";
import { AmenityPickerGrid } from "./AmenityPickerGrid";
import { AMENITIES_HEADING } from "../amenities";

/**
 * Admin "Add a trail to your list" card — Phase A1.
 *
 * Collapses the old Claim + Propose sections into one low-emphasis
 * card that opens on demand. Adding a trail is a once-per-relationship
 * action and shouldn't compete for visual attention with the daily
 * status-set flow above. Closed by default; tap "Add a trail" to
 * expand.
 *
 * Two clear modes inside, on a tabbed segment:
 *   - Search the catalog → file a claim on an existing trail
 *   - Propose a new trail to the catalog
 *
 * Same HIG grouped-inset pattern as the curator workspace.
 */
export function AdminAddTrail({ onChanged }: { onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"claim" | "propose">("claim");

  return (
    <section className="admin-add-card card">
      <button
        type="button"
        className="admin-add-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>
          <strong>+ Add a trail to your list</strong>
          <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
            Claim an existing trail or propose a new one
          </span>
        </span>
        <span aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="admin-add-body">
          <div className="admin-add-tabs">
            <button
              type="button"
              className={`small ${mode === "claim" ? "" : "secondary"}`}
              onClick={() => setMode("claim")}
            >
              Claim an existing trail
            </button>
            <button
              type="button"
              className={`small ${mode === "propose" ? "" : "secondary"}`}
              onClick={() => setMode("propose")}
            >
              Propose a new trail
            </button>
          </div>

          {mode === "claim" ? (
            <ClaimMode onChanged={onChanged} />
          ) : (
            <ProposeMode onChanged={onChanged} />
          )}
        </div>
      )}
    </section>
  );
}

function ClaimMode({ onChanged }: { onChanged: () => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<TrailListItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function search() {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await listTrails({ q: q.trim(), limit: 20 });
      setResults(r.items);
    } finally {
      setSearching(false);
    }
  }

  async function file(trail: TrailListItem) {
    const reason = prompt(
      `Tell the curator why you should administer ${trail.name}. (role, affiliation, etc.)`
    );
    if (reason === null) return;
    setPendingSlug(trail.slug);
    setFeedback(null);
    try {
      await createClaim(trail.slug, reason || undefined);
      setFeedback(`Claim filed for ${trail.name}. A curator will review it.`);
      onChanged();
    } catch (err) {
      setFeedback(
        err instanceof ApiError ? err.message : "Couldn't file the claim."
      );
    } finally {
      setPendingSlug(null);
    }
  }

  return (
    <div className="admin-add-section-body">
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <input
          type="search"
          placeholder="Search by trail name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") search();
          }}
        />
        <button onClick={search} disabled={searching || !q.trim()}>
          {searching ? "Searching…" : "Search"}
        </button>
      </div>
      {results !== null && results.length === 0 && (
        <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
          No matches. Try "Propose a new trail" if it's not in the catalog.
        </p>
      )}
      {results !== null && results.length > 0 && (
        <ul className="admin-claim-list">
          {results.map((t) => (
            <li key={t.slug}>
              <div className="admin-claim-row-main">
                <div className="admin-claim-row-name">{t.name}</div>
                <div className="admin-claim-row-sub muted">
                  {[t.region, t.state].filter(Boolean).join(", ") || "—"}
                </div>
              </div>
              <button
                type="button"
                className="small secondary"
                onClick={() => file(t)}
                disabled={pendingSlug === t.slug}
              >
                {pendingSlug === t.slug ? "Filing…" : "File claim"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {feedback && (
        <div className="admin-feedback admin-feedback-ok">{feedback}</div>
      )}
    </div>
  );
}

function ProposeMode({ onChanged }: { onChanged: () => void }) {
  const [name, setName] = useState("");
  const [statusPageUrl, setStatusPageUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [region, setRegion] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    { kind: "ok" | "err"; msg: string } | null
  >(null);
  const [conflicts, setConflicts] = useState<NameConflict[] | null>(null);
  /** Amenities to bootstrap with the new trail. Worker batch-writes
   *  each as a `amenity.<key>` row in trail_attributes at create time. */
  const [amenities, setAmenities] = useState<Set<string>>(new Set());

  async function submit() {
    setSubmitting(true);
    setFeedback(null);
    setConflicts(null);
    try {
      const r = await proposeTrail({
        name: name.trim(),
        statusPageUrl: statusPageUrl.trim(),
        websiteUrl: websiteUrl.trim() || undefined,
        region: region.trim() || undefined,
        state: stateCode.trim().toUpperCase() || undefined,
        amenities: amenities.size > 0 ? Array.from(amenities) : undefined,
      });
      setFeedback({
        kind: "ok",
        msg: r.deduped
          ? `"${r.trail.name}" was already in the catalog. File a claim on it from the Claim tab.`
          : `"${r.trail.name}" added. Now file a claim on it (Claim tab) so you can manage it.`,
      });
      setName("");
      setStatusPageUrl("");
      setWebsiteUrl("");
      setRegion("");
      setStateCode("");
      setAmenities(new Set());
      onChanged();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.body &&
        typeof err.body === "object"
      ) {
        const body = err.body as { error?: string; conflicts?: NameConflict[] };
        if (Array.isArray(body.conflicts)) {
          setConflicts(body.conflicts);
          setFeedback({ kind: "err", msg: body.error ?? err.message });
          return;
        }
      }
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Couldn't propose the trail.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-add-section-body">
      <div className="drawer-field">
        <span className="drawer-field-label">Trail name *</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="drawer-field">
        <span className="drawer-field-label">Status page URL *</span>
        <input
          type="url"
          value={statusPageUrl}
          placeholder="https://"
          onChange={(e) => setStatusPageUrl(e.target.value)}
        />
        <span className="drawer-field-hint">
          The page that shows whether the trail is open or closed.
        </span>
      </div>
      <div className="drawer-field">
        <span className="drawer-field-label">Website (optional)</span>
        <input
          type="url"
          value={websiteUrl}
          placeholder="https://"
          onChange={(e) => setWebsiteUrl(e.target.value)}
        />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="drawer-field" style={{ flex: 1 }}>
          <span className="drawer-field-label">City (optional)</span>
          <input
            type="text"
            value={region}
            placeholder="e.g. Woodstock"
            onChange={(e) => setRegion(e.target.value)}
          />
        </div>
        <div className="drawer-field" style={{ width: 100 }}>
          <span className="drawer-field-label">State</span>
          <input
            type="text"
            value={stateCode}
            maxLength={2}
            onChange={(e) =>
              setStateCode(e.target.value.toUpperCase().slice(0, 2))
            }
          />
        </div>
      </div>
      <div className="drawer-field">
        <span className="drawer-field-label">{AMENITIES_HEADING}</span>
        <span className="drawer-field-hint">
          Optional — tap any amenity the trail has at the trailhead.
          Riders see them on the trail's detail screen.
        </span>
        <AmenityPickerGrid
          selected={amenities}
          onToggle={(key) => {
            setAmenities((prev) => {
              const next = new Set(prev);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              return next;
            });
          }}
        />
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={submitting || !name.trim() || !statusPageUrl.trim()}
        style={{ marginTop: 4 }}
      >
        {submitting ? "Proposing…" : "Propose trail"}
      </button>
      {feedback && (
        <div
          className={
            feedback.kind === "ok"
              ? "admin-feedback admin-feedback-ok"
              : "admin-feedback admin-feedback-err"
          }
        >
          {feedback.msg}
        </div>
      )}
      {conflicts && conflicts.length > 0 && (
        <div className="admin-conflict-list">
          <strong>Already in the catalog with this name:</strong>
          <ul>
            {conflicts.map((c) => (
              <li key={c.slug}>
                <strong>{c.name}</strong>
                <span className="muted">
                  {" · "}
                  {[c.region, c.state].filter(Boolean).join(" · ") ||
                    "no region/state"}
                </span>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ fontSize: 12 }}>
            If one of these is yours, file a claim on it from the Claim tab.
            Otherwise add a City + State so riders can tell them apart.
          </p>
        </div>
      )}
    </div>
  );
}
